import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AppLayout from '../components/AppLayout';

/**
 * Página de Relatórios (ReportsPage)
 * Exibe a lista dos últimos checkpoints realizados.
 */
function ReportsPage({ editPath = '/checkpoint' }) {
    const navigate = useNavigate();
    const [expandedReports, setExpandedReports] = useState({});

    const { data: reports, isLoading, error } = useQuery({
        queryKey: ['checkpoints', 'with_attendances'],
        queryFn: async () => {
            const response = await api.get('/checkpoints?per_page=6&include_attendances=1');
            return response.data.data.items || [];
        }
    });

    const toggleExpand = (id) => {
        setExpandedReports(prev => ({ ...prev, [id]: !prev[id] }));
    };

    if (isLoading) return <AppLayout><LoadingSpinner size="lg" /></AppLayout>;

    if (error) return <AppLayout><div className="alert alert-error">Erro ao carregar relatórios: {error.message}</div></AppLayout>;

    return (
        <AppLayout>
            <div className="page-container" style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
                <div className="page-header" style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1 className="page-title" style={{ fontSize: '1.5rem', fontWeight: 800 }}>Últimos Relatórios</h1>
                        <p className="page-subtitle" style={{ color: 'var(--checkpoint-muted)' }}>Lista de checkpoints enviados recentemente pelas células.</p>
                    </div>
                    <div>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => navigate('/reports/my-stats')}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>query_stats</span>
                            Minhas Estatísticas
                        </button>
                    </div>
                </div>

                <div className="reports-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {reports.length === 0 ? (
                        <div className="empty-state" style={{ textAlign: 'center', padding: '40px', background: '#f8f9fa', borderRadius: '16px' }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '48px', color: '#ccc' }}>description</span>
                            <p style={{ marginTop: '12px', color: '#666' }}>Nenhum relatório encontrado.</p>
                        </div>
                    ) : (
                        reports.map(report => {
                            const isExpanded = !!expandedReports[report.id];
                            
                            let presentes = [];
                            let ausentes = [];
                            let visitantes = [];

                            if (report.attendances) {
                                presentes = report.attendances.filter(a => (a.status === 'present' || a.status === 'late') && a.member_category !== 'visitor');
                                ausentes = report.attendances.filter(a => a.status === 'absent' || a.status === 'justified');
                                visitantes = report.attendances.filter(a => a.member_category === 'visitor');
                            }

                            return (
                                <div 
                                    key={report.id} 
                                    className="report-card" 
                                    style={{ 
                                        background: 'white', 
                                        padding: '20px', 
                                        borderRadius: '16px', 
                                        border: '1px solid #dfe1e6',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.2s',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div className="report-main" style={{ flex: 1 }}>
                                            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '4px' }}>
                                                {report.notes || `Checkpoint Célula ${report.cell_name || '#' + report.cell_id}`}
                                            </h3>
                                            <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', color: '#666', flexWrap: 'wrap' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>calendar_today</span>
                                                    {new Date(report.checked_at).toLocaleDateString('pt-BR')}
                                                </span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>person</span>
                                                    {report.total_present} Presentes {report.total_visitors > 0 && `{ ${report.total_visitors} Visitantes }`}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="report-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ 
                                                    padding: '4px 12px', 
                                                    borderRadius: '20px', 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: 700, 
                                                    background: report.status === 'submitted' ? '#e3fcef' : '#fff4e5',
                                                    color: report.status === 'submitted' ? '#006644' : '#b27b00'
                                                }}>
                                                    {report.status.toUpperCase()}
                                                </span>
                                                
                                                <button 
                                                    type="button"
                                                    onClick={() => toggleExpand(report.id)}
                                                    style={{ 
                                                        background: 'none', 
                                                        border: 'none', 
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '32px',
                                                        height: '32px',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#f4f5f7',
                                                        color: '#42526e'
                                                    }}
                                                    title={isExpanded ? "Ocultar membros" : "Ver membros"}
                                                >
                                                    <span className="material-symbols-outlined">
                                                        {isExpanded ? 'expand_less' : 'expand_more'}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="report-members-collapse" style={{ marginTop: '16px', borderTop: '1px solid #dfe1e6', paddingTop: '16px', fontSize: '0.85rem', color: '#444' }}>
                                            {!report.attendances ? (
                                                <p style={{ color: '#666', fontStyle: 'italic' }}>Membros não disponíveis.</p>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                    {presentes.length > 0 && (
                                                        <div>
                                                            <strong style={{ color: '#006644' }}>Presentes: </strong>
                                                            {presentes.map(p => p.member_name).join(', ')}
                                                        </div>
                                                    )}
                                                    {ausentes.length > 0 && (
                                                        <div>
                                                            <strong style={{ color: '#bf2600' }}>Ausentes: </strong>
                                                            {ausentes.map(a => `${a.member_name} ${a.justification ? `{${a.justification}}` : ''}`).join(', ')}
                                                        </div>
                                                    )}
                                                    {visitantes.length > 0 && (
                                                        <div>
                                                            <strong style={{ color: '#0052cc' }}>Visitantes: </strong>
                                                            {visitantes.map(v => v.member_name).join(', ')}
                                                        </div>
                                                    )}
                                                    {presentes.length === 0 && ausentes.length === 0 && visitantes.length === 0 && (
                                                        <p style={{ color: '#666', fontStyle: 'italic' }}>Nenhuma presença registrada.</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            type="button"
                                            className="btn btn-outline btn-sm"
                                            onClick={() => navigate(`${editPath}?cell_id=${report.cell_id}&event_id=${report.event_id}`)}
                                            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>edit</span>
                                            Editar Relatório
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </AppLayout>
    );
}

export default ReportsPage;
